import { t } from "@lingui/macro";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ExploreOutlinedIcon from "@mui/icons-material/ExploreOutlined";
import GpsFixedOutlinedIcon from "@mui/icons-material/GpsFixedOutlined";
import LocalFireDepartmentOutlinedIcon from "@mui/icons-material/LocalFireDepartment";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import {
  Box, Button, Chip, Grid, LinearProgress,
  Stack, Tooltip, Typography,
} from "@mui/material";
import { useMemo } from "react";
import { useUser } from "@/client/services/user";

import { type CPView } from "../hooks/useCodingPlatform";
import {
  DifficultyChip, EmptyState, PALETTE, SectionHeader, SectionPaper,
  StatusChip,
} from "../components/shared";
import { isPracticeProblemSubmission } from "../utils/platformSubmissions";
import { resolvePassMeta } from "../utils/skillTestPass";

// ════════════════════════════════════════════════════════════════════════════
//  Types
// ════════════════════════════════════════════════════════════════════════════
type Props = {
  dashboard: any;
  profile: any;
  notifications: any[];
  submissions: any[];
  skillTestSubmissions?: any[];
  recentlyViewedCourses: any[];
  tracks: any[];
  leaderboard: any[];
  studentId: string;
  onNavigate: (v: CPView) => void;
  onLoadProblem: (id: string) => void;
  onOpenCourse?: (id: string) => void;
};

// ════════════════════════════════════════════════════════════════════════════
//  Helpers
// ════════════════════════════════════════════════════════════════════════════
const fmtDelta = (n?: number) => (typeof n === "number" ? (n > 0 ? `+${n}` : `${n}`) : "+0");

const displayNameOf = (user: any, profile: any) => {
  const first = user?.firstName ?? profile?.firstName;
  const last = user?.lastName ?? profile?.lastName;
  if (first || last) return [first, last].filter(Boolean).join(" ").trim();

  const raw = user?.name || profile?.name || user?.username || profile?.displayName || "there";
  const parts = String(raw).trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0]} ${parts[parts.length - 1]}`;
  return parts[0] || "there";
};

const toStartCase = (s: string) =>
  String(s ?? "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const parseDailyChallenge = (dashboard: any) => {
  const dc = dashboard?.dailyChallenge;
  if (!dc) return null;
  if (Array.isArray(dc.problemIds) && dc.problemIds.length) {
    const raw = dc.problemIds[0];
    if (raw && typeof raw === "object") {
      return { ...raw, id: raw._id ?? raw.id, title: raw.title ?? dc.title ?? "Daily Challenge" };
    }
  }
  if (dc._id || dc.id) return { ...dc, id: dc._id ?? dc.id };
  return null;
};

const computeWeeklyStats = (submissions: any[]) => {
  const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const recent = (submissions ?? [])
    .filter(isPracticeProblemSubmission)
    .filter((s) => {
      const t = new Date(s.createdAt).getTime();
      return !isNaN(t) && t >= weekAgo;
    });
  return {
    weeklySolved: recent.filter((s) => s.status === "accepted").length,
    weeklyAttempted: recent.length,
    weeklyXp: recent.reduce((sum, s) => sum + (s.xpEarned ?? 0), 0),
    weeklyRatingDelta: recent.reduce((sum, s) => sum + (s.ratingDelta ?? 0), 0),
  };
};

const xpForDifficulty = (difficulty?: string) => {
  if (difficulty === "advanced") return 150;
  if (difficulty === "intermediate") return 100;
  return 60;
};

/** Local-time YYYY-MM-DD key. Critical: must use local components, not UTC, so the
 *  calendar grid lines up with the user's wall-clock day (the server-provided
 *  activityCalendar used UTC and was off by up to a day for non-UTC users). */
const localDateKey = (input?: string | number | Date) => {
  if (input == null) return null;
  const d = new Date(input);
  if (isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

type CalendarDay = { date: string; accepted: number; submissions: number };

/** Build per-day activity from practice problem submissions + skill-test attempts. */
const buildCalendarActivity = (problemSubs: any[], skillSubs: any[]): CalendarDay[] => {
  const map = new Map<string, CalendarDay>();
  const bump = (key: string, accepted: boolean) => {
    const cur = map.get(key) ?? { date: key, accepted: 0, submissions: 0 };
    cur.submissions += 1;
    if (accepted) cur.accepted += 1;
    map.set(key, cur);
  };

  for (const s of problemSubs ?? []) {
    if (!isPracticeProblemSubmission(s)) continue;
    const key = localDateKey(s.createdAt ?? s.submittedAt);
    if (!key) continue;
    bump(key, s.status === "accepted");
  }

  for (const a of skillSubs ?? []) {
    const key = localDateKey(a.submittedAt ?? a.completedAt ?? a.createdAt);
    if (!key) continue;
    bump(key, resolvePassMeta(a).passed);
  }

  return Array.from(map.values());
};

const parseLocalDateKey = (dateStr: string) => {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d);
};

/** Compute streaks from local-date activity (practice submissions + skill tests). */
const computeStreakFromActivity = (activity: CalendarDay[]) => {
  const activeDays = new Set(activity.filter((d) => d.submissions > 0).map((d) => d.date));
  if (!activeDays.size) return { currentStreak: 0, longestStreak: 0 };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayKey = localDateKey(today)!;

  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const yesterdayKey = localDateKey(yesterday)!;

  let currentStreak = 0;
  let streakStart: Date | null = null;
  if (activeDays.has(todayKey)) streakStart = new Date(today);
  else if (activeDays.has(yesterdayKey)) streakStart = new Date(yesterday);

  if (streakStart) {
    const cursor = new Date(streakStart);
    while (true) {
      const key = localDateKey(cursor);
      if (!key || !activeDays.has(key)) break;
      currentStreak++;
      cursor.setDate(cursor.getDate() - 1);
    }
  }

  const sorted = Array.from(activeDays).sort();
  let longestStreak = 0;
  let run = 0;
  for (let i = 0; i < sorted.length; i++) {
    if (i === 0) {
      run = 1;
    } else {
      const prev = parseLocalDateKey(sorted[i - 1]);
      const cur = parseLocalDateKey(sorted[i]);
      const diffDays = Math.round((cur.getTime() - prev.getTime()) / 86400000);
      run = diffDays === 1 ? run + 1 : 1;
    }
    longestStreak = Math.max(longestStreak, run);
  }

  return { currentStreak, longestStreak };
};

// ════════════════════════════════════════════════════════════════════════════
//  Sub-component: Hero illustration
// ════════════════════════════════════════════════════════════════════════════
function HeroIllustration() {
  return (
    <Box
      aria-hidden
      sx={{
        display: { xs: "none", md: "flex" },
        alignSelf: "stretch",
        alignItems: "stretch",
        justifyContent: "flex-end",
        width: { md: 430, lg: 280 },
        maxWidth: "32%",
        pr: 2,
        flexShrink: 0,
      }}
    >
      <Box
        component="img"
        src="/codingPersonImage.png"
        alt=""
        sx={{
          height: "100%",
          width: "auto",
          maxWidth: "100%",
          objectFit: "contain",
          objectPosition: "right center",
          userSelect: "none",
          pointerEvents: "none",
          display: "block",
        }}
      />
    </Box>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  Sub-component: Activity heat map (GitHub-style: 7 rows × many week columns)
// ════════════════════════════════════════════════════════════════════════════
const HEATMAP_WEEKS = 20;
const HEATMAP_DOW_ROWS = 7;

type HeatmapCell = { key: string; label: string; count: number; inRange: boolean };

const activityShade = (count: number) =>
  count > 3 ? "#1b5e20" : count > 1 ? "#43a047" : count > 0 ? "#a5d6a7" : "#e2e8f0";

function ActivityHeatMap({ activity = [] }: { activity?: CalendarDay[] }) {
  const byDate = useMemo(() => new Map(activity.map((a) => [a.date, a])), [activity]);

  const { weeks, activeDays, totalSubmissions } = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Anchor the last column to the week that contains today so today is always shown.
    const lastWeekStart = new Date(today);
    lastWeekStart.setDate(today.getDate() - today.getDay());

    const firstWeekStart = new Date(lastWeekStart);
    firstWeekStart.setDate(lastWeekStart.getDate() - (HEATMAP_WEEKS - 1) * 7);

    const cols: HeatmapCell[][] = [];
    const cursor = new Date(firstWeekStart);

    for (let w = 0; w < HEATMAP_WEEKS; w++) {
      const col: HeatmapCell[] = [];
      for (let dow = 0; dow < HEATMAP_DOW_ROWS; dow++) {
        const d = new Date(cursor);
        d.setDate(cursor.getDate() + dow);
        const inRange = d <= today;
        const dateKey = localDateKey(d);
        const item = dateKey ? byDate.get(dateKey) : undefined;
        col.push({
          key: dateKey ?? `pad-${w}-${dow}`,
          label: d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
          count: inRange ? (item?.submissions ?? 0) : 0,
          inRange,
        });
      }
      cursor.setDate(cursor.getDate() + 7);
      cols.push(col);
    }

    const flat = cols.flat().filter((c) => c.inRange);
    return {
      weeks: cols,
      activeDays: flat.filter((c) => c.count > 0).length,
      totalSubmissions: flat.reduce((sum, c) => sum + c.count, 0),
    };
  }, [byDate]);

  return (
    <Box sx={{ width: "100%" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography sx={{ fontSize: 11, color: PALETTE.textSecondary }}>
          {t`Last ${HEATMAP_WEEKS} weeks`}
        </Typography>
        <Typography sx={{ fontSize: 11, color: PALETTE.textSecondary }}>
          {t`${activeDays} active days · ${totalSubmissions} submissions`}
        </Typography>
      </Stack>

      <Box sx={{ display: "flex", gap: "3px", width: "100%" }}>
        {weeks.map((col, weekIdx) => (
          <Stack key={weekIdx} spacing="3px" sx={{ flex: 1, minWidth: 0 }}>
            {col.map((cell) => (
              <Tooltip
                key={cell.key}
                title={cell.inRange ? (cell.count > 0 ? `${cell.label} — ${cell.count} submission(s)` : cell.label) : ""}
                arrow
              >
                <Box
                  sx={{
                    width: "100%",
                    aspectRatio: "1",
                    borderRadius: "2px",
                    bgcolor: cell.inRange ? activityShade(cell.count) : "transparent",
                  }}
                />
              </Tooltip>
            ))}
          </Stack>
        ))}
      </Box>

      <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.5} sx={{ mt: 1 }}>
        <Typography sx={{ fontSize: 9, color: PALETTE.textMuted }}>Less</Typography>
        {["#e2e8f0", "#a5d6a7", "#43a047", "#1b5e20"].map((c) => (
          <Box key={c} sx={{ width: 8, height: 8, borderRadius: "2px", bgcolor: c }} />
        ))}
        <Typography sx={{ fontSize: 9, color: PALETTE.textMuted }}>More</Typography>
      </Stack>
    </Box>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  Sub-component: Achievements grid
// ════════════════════════════════════════════════════════════════════════════
const ACHIEVEMENTS: { key: string; title: string; icon: React.ReactNode; color: string; bg: string }[] = [
  { key: "first_solve", title: "First Accepted", icon: <CheckCircleOutlineIcon sx={{ fontSize: 22 }} />, color: "#16a34a", bg: "#dcfce7" },
  { key: "streak_7", title: "7 Day Streak", icon: <LocalFireDepartmentOutlinedIcon sx={{ fontSize: 22 }} />, color: "#ea580c", bg: "#ffedd5" },
  { key: "ten_solved", title: "10 Problems", icon: <WorkspacePremiumOutlinedIcon sx={{ fontSize: 22 }} />, color: "#7c3aed", bg: "#ede9fe" },
  { key: "xp_1000", title: "XP Hunter", icon: <BoltOutlinedIcon sx={{ fontSize: 22 }} />, color: "#2563eb", bg: "#dbeafe" },
  { key: "explorer", title: "Problem Explorer", icon: <ExploreOutlinedIcon sx={{ fontSize: 22 }} />, color: "#ca8a04", bg: "#fef9c3" },
  { key: "consistent", title: "Consistent Coder", icon: <VerifiedOutlinedIcon sx={{ fontSize: 22 }} />, color: "#0d9488", bg: "#ccfbf1" },
];

// ════════════════════════════════════════════════════════════════════════════
//  Main component
// ════════════════════════════════════════════════════════════════════════════
export default function DashboardView(props: Props) {
  const {
    dashboard, profile, submissions, skillTestSubmissions = [],
    recentlyViewedCourses, tracks,
    onNavigate, onLoadProblem, onOpenCourse,
  } = props;

  const { user } = useUser();
  const displayName = displayNameOf(user, profile);
  const analytics = dashboard?.analytics ?? {};
  const profileData = dashboard?.profile ?? profile ?? {};

  // ── Metric values (practice problems only; course lesson submits excluded) ─
  const solved = Number(profileData.solvedCount ?? analytics.solvedCount ?? 0);
  const attempted = Number(profileData.attemptedCount ?? analytics.attemptedCount ?? 0);
  const xp = Number(profileData.xp ?? analytics.xp ?? 0);
  const acceptance = useMemo(() => {
    if (typeof analytics.acceptanceRate === "number") return analytics.acceptanceRate;
    const breakdown: Array<{ _id: string; count: number }> = analytics.statusBreakdown ?? [];
    const total = breakdown.reduce((sum, row) => sum + (row.count ?? 0), 0);
    const accepted = breakdown.find((row) => row._id === "accepted")?.count ?? 0;
    if (total > 0) return Math.round((accepted / total) * 100);
    return attempted > 0 ? Math.round((solved / attempted) * 100) : 0;
  }, [analytics, solved, attempted]);

  // ── Activity calendar data ───────────────────────────────────────────────
  // Build client-side from raw submissions + skill-test attempts, keyed by local
  // date. The server's `dashboard.activityCalendar` is keyed in UTC and only
  // counts practice problem submissions, which caused the off-by-one day and
  // missing skill-test activity in the calendar heatmap.
  const activity = useMemo(
    () => buildCalendarActivity(submissions, skillTestSubmissions),
    [submissions, skillTestSubmissions],
  );

  const { currentStreak, longestStreak } = useMemo(() => {
    const computed = computeStreakFromActivity(activity);
    const profileLongest = Number(profileData.longestStreak ?? 0);
    return {
      currentStreak: computed.currentStreak,
      longestStreak: Math.max(computed.longestStreak, profileLongest),
    };
  }, [activity, profileData.longestStreak]);

  const { weeklySolved, weeklyAttempted, weeklyXp } = computeWeeklyStats(submissions);

  // ── Daily Challenge ───────────────────────────────────────────────────────
  const dailyChallenge = useMemo(() => parseDailyChallenge(dashboard), [dashboard]);

  // ── Continue Coding items (in-progress courses) ───────────────────────────
  const continueItems = useMemo(() => (
    (recentlyViewedCourses ?? [])
      .filter((c) => (c.progressPercent ?? 0) < 100)
      .slice(0, 2)
      .map((c) => ({
        id: c.id ?? c._id,
        title: c.title ?? "Course",
        type: "Course",
        total: c.totalLessons ?? 0,
        progress: (c.progressPercent ?? 0) / 100,
        kind: "course" as const,
      }))
  ), [recentlyViewedCourses]);

  // ── Achievements set ──────────────────────────────────────────────────────
  const earnedBadges = useMemo(() => {
    const ids = new Set<string>();
    (profileData?.badges ?? []).forEach((b: any) => ids.add(typeof b === "string" ? b : (b.id ?? b)));
    if (solved >= 25) ids.add("explorer");
    if (currentStreak >= 3) ids.add("consistent");
    return ids;
  }, [profileData, solved, currentStreak]);

  // ── Recent submissions (top 5) ────────────────────────────────────────────
  const recentSubs = useMemo(() => {
    const fromDashboard = dashboard?.recentSubmissions;
    const list = Array.isArray(fromDashboard) && fromDashboard.length ? fromDashboard : (submissions ?? []);
    return list.slice(0, 5);
  }, [dashboard, submissions]);

  // ── Learning paths (top 5) ───────────────────────────────────────────────
  const learningPaths = useMemo(() => (tracks ?? []).slice(0, 5), [tracks]);

  // ── Last problem (for "Continue Last Coding" CTA) ─────────────────────────
  const lastProblem = useMemo(() => {
    const last = recentSubs.find(isPracticeProblemSubmission);
    if (last?.problemId) {
      const p = last.problemId;
      return { id: p._id ?? p.id ?? p, title: p.title ?? "Problem" };
    }
    return null;
  }, [recentSubs]);

  // ══════════════════════════════════════════════════════════════════════════
  //  Render
  // ══════════════════════════════════════════════════════════════════════════
  return (
    <Box sx={{ fontSize: 13, maxWidth: 1320, mx: "auto" }}>
      {/* ───────────────────── Hero / Welcome banner ─────────────────────── */}
      <SectionPaper sx={{
        mb: 1.25,
        overflow: "hidden",
        background: "linear-gradient(135deg, #f8fbff 0%, #eff6ff 45%, #f0f9ff 100%)",
        borderColor: "#dbeafe",
      }}>
        <Stack direction="row" alignItems="stretch" justifyContent="flex-start" sx={{ pl: 3.75, pr: 0, py: 0, gap: 65 }}>
          <Box sx={{ py: 1.75 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: "primary.main", mb: 0.25 }}>
              {t`Welcome back, ${displayName}!`} <Box component="span" sx={{ ml: 0.5 }}>👋</Box>
            </Typography>
            <Typography sx={{ fontSize: 12, color: PALETTE.textSecondary, mb: 1.25 }}>
              {t`Keep coding, keep improving and achieve your goals.`}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                size="small"
                onClick={() => lastProblem && onLoadProblem(String(lastProblem.id))}
                disabled={!lastProblem}
                startIcon={<PlayArrowRoundedIcon sx={{ fontSize: 16 }} />}
                sx={{ textTransform: "none", fontSize: 12, fontWeight: 700, borderRadius: "6px", px: 1.5, py: 0.5 }}
              >
                {t`Continue Last Coding`}
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => onNavigate("Problems")}
                startIcon={<ExploreOutlinedIcon sx={{ fontSize: 16 }} />}
                sx={{ textTransform: "none", fontSize: 12, fontWeight: 700, borderRadius: "6px", px: 1.5, py: 0.5, borderColor: PALETTE.borderDark, color: "primary.main", "&:hover": { borderColor: "#90caf9", bgcolor: PALETTE.bgHover } }}
              >
                {t`Explore Problems`}
              </Button>
            </Stack>
          </Box>
          <HeroIllustration />
        </Stack>
      </SectionPaper>

      {/* ───────────────────── Metric cards ────────────────────────────── */}
      <Grid container spacing={1} sx={{ mb: 1.25 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricBox
            icon={<CheckCircleOutlineIcon sx={{ fontSize: 15 }} />}
            color="#16a34a" bg="#dcfce7"
            label={t`Solved`} value={solved} delta={fmtDelta(weeklySolved)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricBox
            icon={<GpsFixedOutlinedIcon sx={{ fontSize: 15 }} />}
            color="#2563eb" bg="#dbeafe"
            label={t`Attempted`} value={attempted} delta={fmtDelta(weeklyAttempted)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricBox
            icon={<TrendingUpOutlinedIcon sx={{ fontSize: 15 }} />}
            color="#7c3aed" bg="#ede9fe"
            label={t`Acceptance Rate`} value={`${acceptance}%`}
          />
        </Grid>
        {/* <Grid item xs={12} sm={6} md={2}>
          <MetricBox
            icon={<StarRoundedIcon sx={{ fontSize: 15 }} />}
            color="#ca8a04" bg="#fef9c3"
            label={t`Current Rating`} value={rating} delta={fmtDelta(weeklyRatingDelta)}
          />
        </Grid> */}
        <Grid item xs={12} sm={6} md={3}>
          <MetricBox
            icon={<LocalFireDepartmentOutlinedIcon sx={{ fontSize: 15 }} />}
            color="#ea580c" bg="#ffedd5"
            label={t`Current Streak`} value={`${currentStreak} Days`}
            sub={t`Best: ${longestStreak} Days`}
          />
        </Grid>
        {/* <Grid item xs={12} sm={6} md={2}>
          <MetricBox
            icon={<BoltOutlinedIcon sx={{ fontSize: 15 }} />}
            color="#0d9488" bg="#ccfbf1"
            label={t`XP Earned`} value={xp.toLocaleString()} delta={fmtDelta(weeklyXp)}
          />
        </Grid> */}
      </Grid>

      {/* ───────────────────── Daily Challenge | Continue Coding | Activity Heat Map ──── */}
      <Grid container spacing={1} sx={{ mb: 1.25 }}>
        {/* Daily Challenge */}
        <Grid item xs={12} md={4} sx={{ display: "flex" }}>
          <SectionPaper sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
            <SectionHeader title={t`Daily Challenge`} action={<ViewAll onClick={() => onNavigate("Problems")} />} />
            <Box sx={{ p: 1.25, flex: 1, display: "flex", flexDirection: "column" }}>
              {dailyChallenge ? (
                <>
                  <Typography sx={{ fontSize: 10, color: PALETTE.textSecondary, fontWeight: 600, mb: 0.25 }}>
                    {t`Today's Challenge`}
                  </Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: "primary.main", mb: 0.75, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {dailyChallenge.title ?? "Daily Challenge"}
                  </Typography>
                  <Stack direction="row" spacing={0.75} sx={{ mb: 1.25, flexWrap: "wrap" }}>
                    {dailyChallenge.difficulty && <DifficultyChip difficulty={dailyChallenge.difficulty} />}
                    {(dailyChallenge.tags ?? dailyChallenge.topics ?? []).slice(0, 1).map((tg: string) => (
                      <Chip key={tg} size="small" label={toStartCase(tg)} sx={{ fontSize: 11, height: 22, bgcolor: "#eff6ff", color: "#1d4ed8" }} />
                    ))}
                  </Stack>
                  <Grid container spacing={1} sx={{ mb: 1.5 }}>
                    <Grid item xs={4}>
                      <StatCell label={t`XP Reward`} value={`${dailyChallenge.xp ?? xpForDifficulty(dailyChallenge.difficulty)} XP`} />
                    </Grid>
                    <Grid item xs={4}>
                      <StatCell label={t`Est. Time`} value={dailyChallenge.estimatedMinutes ? `${dailyChallenge.estimatedMinutes} mins` : dailyChallenge.estTimeMins ? `${dailyChallenge.estTimeMins} mins` : "—"} />
                    </Grid>
                    <Grid item xs={4}>
                      <StatCell label={t`Success Rate`} value={typeof dailyChallenge.acceptanceRate === "number" ? `${dailyChallenge.acceptanceRate}%` : "—"} />
                    </Grid>
                  </Grid>
                  <Button fullWidth variant="contained" size="small" onClick={() => onLoadProblem(String(dailyChallenge.id ?? dailyChallenge._id))}
                    sx={{ textTransform: "none", fontSize: 12, fontWeight: 700, borderRadius: "6px", py: 0.6, mt: "auto" }}>
                    {t`Solve Now`}
                  </Button>
                </>
              ) : (
                <EmptyState message={t`No challenge available right now.`} />
              )}
            </Box>
          </SectionPaper>
        </Grid>

        {/* Continue Coding */}
        <Grid item xs={12} md={4} sx={{ display: "flex" }}>
          <SectionPaper sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
            <SectionHeader title={t`Continue Coding`} action={<ViewAll onClick={() => onNavigate("Courses")} />} />
            <Stack spacing={1} sx={{ p: 1.25, flex: 1 }}>
              {continueItems.length === 0 && (
                <EmptyState message={t`No in-progress courses yet — enroll in a course to continue learning.`} />
              )}
              {continueItems.slice(0, 2).map((item: any) => {
                const id = item.id ?? item._id;
                const total = item.total ?? item.questions ?? 0;
                const pct = item.progress != null ? Math.round(item.progress * 100) : (total ? Math.round(((item.solved ?? 0) / total) * 100) : 0);
                const handleContinue = () => {
                  if (item.kind === "course") onOpenCourse?.(String(id));
                  else onLoadProblem(String(id));
                };
                return (
                  <Box key={id} sx={{ p: 1, borderRadius: "6px", border: `1px solid ${PALETTE.border}`, bgcolor: "#fff" }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: "primary.main", mb: 0.4 }} noWrap>
                      {item.title ?? "Practice"}
                    </Typography>
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
                      <Typography sx={{ fontSize: 11, color: PALETTE.textSecondary }}>{item.type ?? t`Course`}</Typography>
                      {total > 0 && (
                        <>
                          <Box sx={{ width: 3, height: 3, borderRadius: "50%", bgcolor: PALETTE.textMuted }} />
                          <Typography sx={{ fontSize: 11, color: PALETTE.textSecondary }}>{t`${total} Lessons`}</Typography>
                        </>
                      )}
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <LinearProgress variant="determinate" value={pct} sx={{ flex: 1, height: 5, borderRadius: 3 }} color={pct >= 100 ? "success" : "primary"} />
                      <Typography sx={{ fontSize: 11, fontWeight: 700, minWidth: 32, textAlign: "right" }}>{pct}%</Typography>
                      <Button size="small" variant="outlined" onClick={handleContinue}
                        sx={{ textTransform: "none", fontSize: 11, py: 0.25, px: 1.25, minWidth: 0, borderColor: PALETTE.borderDark, color: "primary.main" }}>
                        {t`Continue`}
                      </Button>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </SectionPaper>
        </Grid>

        {/* Activity Heat Map */}
        <Grid item xs={12} md={4} sx={{ display: "flex" }}>
          <SectionPaper sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
            <SectionHeader title={t`Activity Heat Map`} />
            <Box sx={{ px: 1.25, pb: 1.25, pt: 0.5, flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <ActivityHeatMap activity={activity} />
            </Box>
          </SectionPaper>
        </Grid>
      </Grid>

      {/* ───────────────────── Recent Submissions | Learning Paths | Achievements ─ */}
      <Grid container spacing={1} sx={{ mb: 1.25 }}>
        {/* Recent Submissions */}
        <Grid item xs={12} md={4}>
          <SectionPaper sx={{ height: "100%" }}>
            <SectionHeader title={t`Recent Submissions`} action={<ViewAll onClick={() => onNavigate("Submissions")} />} />
            <Stack divider={<Box sx={{ borderBottom: `1px solid ${PALETTE.border}` }} />}>
              {recentSubs.length === 0 && <EmptyState message={t`No submissions yet.`} />}
              {recentSubs.map((s: any) => (
                <Box key={s._id} sx={{ px: 1.25, py: 0.75 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.25 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: "primary.main", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 180 }}
                      title={s.problemId?.title ?? "Problem"}>
                      {s.problemId?.title ?? "Problem"}
                    </Typography>
                    <StatusChip status={s.status} />
                  </Stack>
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <Typography sx={{ fontSize: 11, color: PALETTE.textMuted, textTransform: "capitalize" }}>
                      {s.programmingLanguage ?? s.language ?? "—"}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: PALETTE.textMuted }}>·</Typography>
                    <Typography sx={{ fontSize: 11, color: PALETTE.textMuted }}>{s.runtimeMs ? `${(s.runtimeMs / 1000).toFixed(2)}s` : "—"}</Typography>
                    <Box sx={{ flex: 1 }} />
                    <Typography sx={{ fontSize: 10, color: PALETTE.textMuted }}>{formatRelative(s.createdAt)}</Typography>
                  </Stack>
                </Box>
              ))}
            </Stack>
          </SectionPaper>
        </Grid>

        {/* Learning Paths */}
        <Grid item xs={12} md={4}>
          <SectionPaper sx={{ height: "100%" }}>
            <SectionHeader title={t`Learning Paths`} action={<ViewAll onClick={() => onNavigate("Tracks")} />} />
            <Box sx={{ p: 1.25 }}>
              {learningPaths.length === 0 && <EmptyState message={t`No tracks available yet.`} />}
              {learningPaths.map((track: any) => {
                const pct = track.progressPercent
                  ?? (track.attempted ? Math.round(((track.solved ?? 0) / track.attempted) * 100) : 0);
                const color: "success" | "primary" | "warning" | "error" =
                  pct >= 100 ? "success" : pct >= 60 ? "primary" : pct >= 30 ? "warning" : "error";
                return (
                  <Box key={track.id ?? track._id ?? track.title} sx={{ mb: 1.25, "&:last-child": { mb: 0 } }}>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.4 }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#1f2937" }} noWrap>
                        {track.title ?? track.topic ?? "Track"}
                      </Typography>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: pct >= 100 ? "success.main" : "primary.main" }}>{pct}%</Typography>
                    </Stack>
                    <LinearProgress variant="determinate" value={pct} color={color} sx={{ height: 6, borderRadius: 3 }} />
                    {track.totalSteps != null && (
                      <Typography sx={{ fontSize: 10, color: PALETTE.textMuted, mt: 0.4 }}>
                        {track.completedSteps ?? 0}/{track.totalSteps} milestones
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          </SectionPaper>
        </Grid>

        {/* Achievements */}
        <Grid item xs={12} md={4}>
          <SectionPaper sx={{ height: "100%" }}>
            <SectionHeader title={t`Achievements`} />
            <Box sx={{ p: 1.25 }}>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 0.75 }}>
                {ACHIEVEMENTS.map((a) => {
                  const earned = earnedBadges.has(a.key);
                  return (
                    <Tooltip key={a.key} title={earned ? a.title : `${a.title} — locked`} arrow>
                      <Box sx={{
                        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                        py: 0.75, px: 0.5, borderRadius: "8px",
                        border: `1px solid ${PALETTE.border}`,
                        bgcolor: earned ? "#fff" : "#f8fafc",
                        opacity: earned ? 1 : 0.55,
                        transition: "transform 0.15s",
                        "&:hover": { transform: earned ? "translateY(-2px)" : "none" },
                      }}>
                        <Box sx={{ width: 32, height: 32, borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: earned ? a.bg : "#e2e8f0", color: earned ? a.color : PALETTE.textMuted }}>
                          {a.icon}
                        </Box>
                        <Typography sx={{ fontSize: 9, fontWeight: 600, color: earned ? "#1f2937" : PALETTE.textSecondary, mt: 0.4, textAlign: "center", lineHeight: 1.15 }}>
                          {a.title}
                        </Typography>
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            </Box>
          </SectionPaper>
        </Grid>
      </Grid>
    </Box>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  Sub-components (private to this file)
// ════════════════════════════════════════════════════════════════════════════

function MetricBox({ icon, color, bg, label, value, delta, sub }: {
  icon: React.ReactNode; color: string; bg: string; label: string; value: string | number; delta?: string; sub?: string;
}) {
  return (
    <SectionPaper sx={{ height: "100%" }}>
      <Box sx={{ p: 1.15 }}>
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.6 }}>
          <Box sx={{ width: 26, height: 26, borderRadius: "6px", bgcolor: bg, color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            {icon}
          </Box>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: PALETTE.textSecondary, textTransform: "capitalize", lineHeight: 1.2 }}>{label}</Typography>
        </Stack>
        <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#1f2937", lineHeight: 1.1 }}>{value}</Typography>
        {delta && <Typography sx={{ fontSize: 10, color: "#16a34a", fontWeight: 600, mt: 0.2 }}>{delta} {t`this week`}</Typography>}
        {sub && <Typography sx={{ fontSize: 10, color: PALETTE.textMuted, mt: 0.2 }}>{sub}</Typography>}
      </Box>
    </SectionPaper>
  );
}

function ViewAll({ onClick }: { onClick: () => void }) {
  return (
    <Button size="small" onClick={onClick} sx={{ textTransform: "none", fontSize: 11, fontWeight: 700, color: "primary.main", minWidth: 0, p: 0.25 }}>
      {t`View All`}
    </Button>
  );
}

function StatCell({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ textAlign: "center", p: 0.5, borderRadius: "5px", bgcolor: PALETTE.bgLight }}>
      <Typography sx={{ fontSize: 9, color: PALETTE.textSecondary, fontWeight: 600, mb: 0.2, textTransform: "capitalize" }}>{label}</Typography>
      <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#1f2937" }}>{value}</Typography>
    </Box>
  );
}

function formatRelative(input?: string) {
  if (!input) return "—";
  const d = new Date(input);
  if (isNaN(d.getTime())) return "—";
  const diffMs = Date.now() - d.getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}
